import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { Field, inputCls } from "./fields";

const SOCIALS = ["facebook", "instagram", "spotify", "apple", "youtube", "tiktok"];
const LABELS = { apple: "apple music / itunes" };

export default function Settings() {
  const [socials, setSocials] = useState({});
  const [vouchers, setVouchers] = useState([]);
  const [newV, setNewV] = useState({ code: "", percent: "", expires: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setSocials(r.data?.socials || {});
      setVouchers(r.data?.vouchers || []);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { socials, vouchers });
      toast.success("Settings saved");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const addVoucher = () => {
    const code = newV.code.trim().toUpperCase();
    const pct = parseInt(newV.percent, 10);
    if (!code || !pct || pct < 1 || pct > 100) {
      toast.error("Enter a code and a percent between 1 and 100");
      return;
    }
    if (vouchers.some((v) => v.code === code)) {
      toast.error("That code already exists");
      return;
    }
    setVouchers([...vouchers, { code, percent: pct, expires: newV.expires || "", active: true }]);
    setNewV({ code: "", percent: "", expires: "" });
  };

  return (
    <div data-testid="admin-settings">
      <h1 className="font-serif text-2xl text-slate-50 mb-2">Site Settings</h1>
      <p className="text-sm text-slate-400 font-light mb-8 max-w-lg">
        Social links shown across the site. YouTube and TikTok icons stay hidden until a URL is added here.
        The release countdown banner lives at the top of the Releases tab.
      </p>
      <div className="max-w-xl rounded-2xl border border-gold-400/20 bg-navy-900/50 p-6" data-testid="voucher-settings">
        <h2 className="font-serif text-xl text-slate-50 mb-1">Discount Vouchers</h2>
        <p className="text-xs text-slate-400 font-light mb-6">
          Codes customers enter on the bespoke enquiry form to take a percentage off the quoted total.
          Add an expiry date to make a code stop working after a day of your choice, or leave it blank to keep it running.
        </p>
        {vouchers.length > 0 && (
          <div className="space-y-2 mb-6">
            {vouchers.map((v) => (
              <div key={v.code} className="flex items-center gap-3 rounded-xl border border-white/5 bg-navy-950/60 px-4 py-3"
                data-testid={`voucher-row-${v.code}`}>
                <span className="font-mono text-sm text-gold-300 flex-1">{v.code}</span>
                <span className="text-xs text-slate-300">−{v.percent}%</span>
                <span className="text-[11px] text-slate-500">{v.expires ? `expires ${v.expires}` : "no expiry"}</span>
                <label className="flex items-center gap-1.5 text-[11px] text-slate-400 cursor-pointer">
                  <input type="checkbox" className="accent-gold-400" checked={v.active}
                    onChange={(e) => setVouchers(vouchers.map((x) => x.code === v.code ? { ...x, active: e.target.checked } : x))}
                    data-testid={`voucher-toggle-${v.code}`} />
                  live
                </label>
                <button onClick={() => setVouchers(vouchers.filter((x) => x.code !== v.code))}
                  className="text-slate-500 hover:text-red-400 transition-colors p-1" data-testid={`voucher-delete-${v.code}`}>
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_90px_150px_auto] gap-3 items-end">
          <Field label="Code">
            <input className={`${inputCls} uppercase`} value={newV.code} placeholder="MM15%30OCT"
              onChange={(e) => setNewV({ ...newV, code: e.target.value.toUpperCase() })} data-testid="voucher-code-input" />
          </Field>
          <Field label="% off">
            <input type="number" min="1" max="100" className={inputCls} value={newV.percent} placeholder="15"
              onChange={(e) => setNewV({ ...newV, percent: e.target.value })} data-testid="voucher-percent-input" />
          </Field>
          <Field label="Expires (optional)">
            <input type="date" className={inputCls} value={newV.expires}
              onChange={(e) => setNewV({ ...newV, expires: e.target.value })} data-testid="voucher-expires-input" />
          </Field>
          <button onClick={addVoucher} className="btn-ghost-gold !px-5 !py-2.5 text-xs" data-testid="voucher-add-btn">
            Add Voucher
          </button>
        </div>
      </div>

      <h2 className="font-serif text-xl text-slate-50 mt-12 mb-4">Social Links</h2>
      <div className="max-w-xl space-y-5">
        {SOCIALS.map((k) => (
          <Field key={k} label={LABELS[k] || k}>
            <input className={inputCls} value={socials[k] || ""} placeholder={`https://…`}
              onChange={(e) => setSocials({ ...socials, [k]: e.target.value })} data-testid={`settings-${k}-input`} />
          </Field>
        ))}
      </div>

      <button onClick={save} disabled={saving} className="btn-gold !px-6 !py-2.5 text-xs disabled:opacity-50 mt-6" data-testid="settings-save-btn">
        {saving ? "Saving…" : "Save Settings"}
      </button>
    </div>
  );
}
